// Angular
import { Inject, Injectable } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

// Local library
import { CommonFacade, ENVIRONMENT, SessionService } from '@amwager/data-access';
import { MatNotificationService } from '@amwager/util';

// Local file
import { FundingFacade } from '../+state';
import { FundingService } from './funding.service';

/** Business logic for the deposit debit/credit card component. */
@Injectable()
export class DepositDebitCreditCardService {

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly sessionService: SessionService,
    private readonly fundingFacade: FundingFacade,
    private readonly commonFacade: CommonFacade,
    private readonly matNotificationService: MatNotificationService,
    private readonly fundingService: FundingService,
    @Inject(ENVIRONMENT) private readonly environment: string,
  ) {}

  /** Build the deposit form. */
  buildForm(): FormGroup {
    return this.formBuilder.group({
      amount: ['', [Validators.required, Validators.minLength(25), Validators.maxLength(500)]]
    });
  }

  /** Emits when a card was added successfully. */
  isAddCardSuccess$() {
    return this.fundingFacade.isAddDebitCreditCardSuccess$();
  }

  /** Card fee stream. */
  cardFee$() {
    return this.fundingFacade.getCardFee$();
  }

  /** Load the saved cards. */
  loadCards$() {
    this.fundingFacade.getDebitCreditCard();
    return this.fundingFacade.getListCards$();
  }

  /** Current TMS id stream. */
  tmsId$() {
    return this.commonFacade?.tmsId$();
  }

  /** Request the card fee for a credit-card amount. */
  setCardFee(amount: number): void {
    this.fundingFacade.setCardFee(true, amount, 'CreditCard');
  }

  /** Ask the user to confirm removing a card. */
  confirmRemoveCard$() {
    return this.matNotificationService.showAlert(
      'Confirmation',
      'Are you sure about removing this bank account?',
      undefined,
      'Yes',
      'Cancel'
    );
  }

  /** Delete a card and stream the result. */
  delete$(orderId: string) {
    this.fundingFacade.deleteDebitCreditCard(orderId);
    return this.fundingFacade.isDeleteDebitCreditCardSuccess$();
  }

  /** Build the unique eProtect registration message (max lengths preserved). */
  buildRegistrationMessage(): { id: string; orderId: string } {
    const id = this.sessionService.generateSessionId();
    const orderId = this.sessionService.generateSessionId();

    return {
      id: btoa(id).substring(0, 24),      // unique, max 25
      orderId: btoa(orderId).substring(0, 24), // unique, max 256
    };
  }

  /** Dispatch the card deposit and stream the result. */
  deposit$(amount: number, orderId: string, tmsId: string) {
    this.fundingFacade.depositDebitCreditCard(amount, orderId, tmsId);
    return this.fundingFacade.isDepositDebitCreditCardSuccess$();
  }

  /** Flag the app as offline for the given source. */
  setOffline(source: string): void {
    this.commonFacade.setIsConnectedInternet(source, false);
  }

  /** Mark all controls touched to reveal validation messages. */
  markFormGroupTouched(formGroup: FormGroup): void {
    this.fundingService.markFormGroupTouched(formGroup);
  }

  /** Show a single-button alert. */
  showAlert(header: string, message: string): void {
    this.matNotificationService.showAlert(header, message, undefined, undefined, 'Close');
  }

  /** Watch connectivity and report changes back to the caller. */
  checkConnectivity(source: string, onChange: (online: boolean) => void): void {
    this.fundingService.checkConnectivity(this.environment, source, onChange);
  }

}
